import type { CityConfig } from "../city-template/types";

export const laPlataConfig: CityConfig = {
  city: "La Plata",
  state: "Maryland",
  stateAbbr: "MD",
  slug: "la-plata",
  route: "/la-plata",
  title: "Emergency Passport La Plata, MD — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in La Plata, Maryland — as fast as 24 hours. Fast passport renewal and new passport applications for La Plata, Waldorf, White Plains, and Charles County. A+ BBB rated.",
  h1: "Emergency & Expedited Passport Services in La Plata, MD",
  heroSubtitle: "La Plata residents — from Charles County government professionals to Southern Maryland families — trust Fast Passport Center for emergency and expedited passports as fast as 24 hours. A+ BBB rated courier service serving Charles County.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Charming%20small%20town%20downtown%20La%20Plata%20Maryland%20courthouse%20square%20with%20brick%20sidewalks%2C%20historic%20county%20buildings%2C%20American%20flags%2C%20tree-lined%20streets%2C%20warm%20afternoon%20sunlight%2C%20professional%20small%20town%20photography%2C%20no%20visible%20text%20or%20people%2C%20red%20brick%20and%20white%20trim%20architecture%20with%20green%20lawns&width=1800&height=700&seq=la-plata-hero-001&orientation=landscape",
  heroImageAlt: "Historic courthouse square view of La Plata Maryland with charming brick downtown architecture",
  geoPosition: "38.5293;-76.9783",
  geoPlacename: "La Plata, Maryland",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Washington DC Passport Agency",
    address: "600 19th St NW, 1st Floor, Washington, DC 20006",
    distance: "Approximately 45–55 minutes from La Plata via MD-210 and I-495",
  },
  nearbyCitiesZips: [
    { name: "La Plata", zips: "20646" },
    { name: "Waldorf", zips: "20601, 20602, 20603, 20604" },
    { name: "White Plains", zips: "20695" },
    { name: "Indian Head", zips: "20640" },
    { name: "Brandywine", zips: "20613" },
    { name: "Bryans Road", zips: "20616" },
    { name: "Accokeek", zips: "20607" },
    { name: "Pomfret", zips: "20675" },
    { name: "Hughesville", zips: "20637" },
    { name: "Port Tobacco", zips: "20677" },
    { name: "Waldorf", zips: "20601, 20602, 20603, 20604" },
    { name: "St. Charles", zips: "20601, 20602, 20603" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier for La Plata and Charles County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-government-line", title: "Charles County Hub", desc: "Serving county government and Southern Maryland families" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels — no long drive to DC" },
  ],
  internalLinks: [
    { name: "Expedited passport in Waldorf, MD", route: "/waldorf" },
    { name: "Emergency passport help in Bowie, MD", route: "/bowie" },
    { name: "Urgent passport services in Maryland", route: "/maryland" },
  ],
  fpcOfficeNote: "Fast Passport Center serves La Plata, Maryland remotely via secure registered courier. We do not have a walk-in office in La Plata — our registered couriers hand-deliver your documents to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for La Plata travelers without the long drive to downtown DC.",
  fpcOfficeAddress: "401 E Pratt St, Baltimore, Maryland 21202",
  fpcOfficeDistance: "Serving La Plata, Waldorf, White Plains, Indian Head, and greater Charles County",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout the La Plata area and Charles County. From the historic courthouse square downtown to the growing suburban neighborhoods, from the naval community near Indian Head to the rural areas along the Potomac — we serve all of Charles County's communities.",
  nearbyCitiesIntro: "We serve La Plata and nearby communities including Waldorf, White Plains, Indian Head, Brandywine, Bryans Road, Accokeek, Pomfret, Hughesville, Port Tobacco, and St. Charles. We also serve Prince George's County residents in Accokeek and Brandywine.",
  howItWorksIntro: "Most La Plata travelers complete everything online and ship documents overnight. No need to drive 45–55 minutes to downtown DC through heavy traffic.",
  mistakesIntro: "La Plata is the county seat of Charles County with a mix of government professionals, military families, and Southern Maryland residents. Here are the most common passport mistakes we see from La Plata travelers.",
  testimonialsIntro: "La Plata travelers — from county employees to military families to Southern Maryland residents — trust us when their departure is days away.",
  faqIntro: "Have questions about expedited passports in La Plata, MD? Here are the most common ones we hear from Charles County travelers.",
  statePageRoute: "/states/maryland",
  statePageName: "Maryland",
  dmvWebsite: "https://mva.maryland.gov",
  dmvLabel: "Maryland MVA — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Perfect for Southern Maryland travel deadlines", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Must drive 45–55 min to downtown DC", "Full day commitment — traffic is unpredictable"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For La Plata travelers with immediate departure needs — as fast as 24 hours. Perfect for military families, government travel, and last-minute trips.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Military and government travel support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for La Plata adults with a passport issued within the last 15 years.",
      bullets: ["No clerk visit required for most renewals", "Mail-in or courier options", "Returned to La Plata address"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport (First-Time)",
      description: "First-time passport application for La Plata residents and families — including new professionals and young families.",
      bullets: ["Acceptance facility scheduling", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Emergency passport replacement for La Plata residents who lost their passport before travel.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement in 24–48 hours"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 — for La Plata families with kids traveling internationally.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change Passport",
      description: "Passport name correction after marriage, divorce, or legal name changes for La Plata residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport La Plata MD", description: "Urgent passport processing for La Plata Maryland travelers with immediate departure needs." },
    { name: "Expedited Passport Renewal La Plata MD", description: "Fast passport renewal for La Plata Maryland residents with a previously-issued passport." },
    { name: "First-Time Passport La Plata MD", description: "New passport application services for La Plata Maryland residents applying for their first passport." },
    { name: "Child Passport La Plata MD", description: "Minor passport services for La Plata Maryland families with children under 16." },
  ],
  testimonials: [
    {
      name: "Thomas K.",
      location: "La Plata, MD",
      rating: 5,
      text: "Military deployment required updated passport with 4 days notice. They understood the urgency and had it in my hands within 36 hours. Professional service that knows military timelines.",
      service: "Emergency Passport",
    },
    {
      name: "Angela M.",
      location: "Waldorf, MD",
      rating: 5,
      text: "Family cruise to the Bahamas with 9 days notice. Three passports processed in under a week. We shipped from the La Plata UPS Store and everything arrived perfectly.",
      service: "Family Passport Bundle",
    },
    {
      name: "Robert D.",
      location: "White Plains, MD",
      rating: 5,
      text: "Business trip to London with 7 days notice. Fast Passport Center had my renewed passport ready in 2 days. No driving to DC — everything shipped overnight.",
      service: "Passport Renewal",
    },
    {
      name: "Jennifer S.",
      location: "Indian Head, MD",
      rating: 5,
      text: "Last-minute family trip to Mexico. They walked us through the child passport requirements and had everything ready in 5 days. Couldn't have done it without them.",
      service: "Child Passport",
    },
  ],
  facilities: [
    {
      id: "la-plata-1",
      name: "La Plata Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "40 St Mary's Ave",
      city: "La Plata",
      state: "MD",
      zip: "20646",
      phone: "(301) 934-2100",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Downtown La Plata, near the courthouse square",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary La Plata acceptance facility. Appointments required — book 1–2 weeks in advance. Convenient for downtown and county government employees.",
      icon: "ri-mail-line",
    },
    {
      id: "la-plata-2",
      name: "Waldorf Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "3250 Leonardtown Rd",
      city: "Waldorf",
      state: "MD",
      zip: "20601",
      phone: "(301) 645-2225",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Waldorf, approximately 8 miles north of La Plata",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Waldorf and northern Charles County residents. Good alternative when La Plata appointments are full.",
      icon: "ri-mail-line",
    },
    {
      id: "la-plata-3",
      name: "Indian Head Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "20 Strauss Ave",
      city: "Indian Head",
      state: "MD",
      zip: "20640",
      phone: "(301) 743-5110",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Indian Head, approximately 10 miles west of La Plata",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Indian Head and western Charles County residents including the naval community. Ample parking available.",
      icon: "ri-mail-line",
    },
    {
      id: "la-plata-4",
      name: "White Plains Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "4560 Crain Hwy",
      city: "White Plains",
      state: "MD",
      zip: "20695",
      phone: "(301) 843-5000",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "White Plains, approximately 6 miles north of La Plata",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serving White Plains and northern Charles County residents. Good alternative when La Plata appointments are full.",
      icon: "ri-mail-line",
    },
    {
      id: "la-plata-5",
      name: "Charles County Clerk's Office",
      type: "County Clerk Passport Acceptance",
      address: "200 Charles St",
      city: "La Plata",
      state: "MD",
      zip: "20646",
      phone: "(301) 932-3202",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "Downtown La Plata, at the courthouse",
      website: "https://www.charlescountymd.gov/",
      websiteLabel: "Charles County",
      notes: "County-level facility with full passport acceptance. Extended hours compared to post office locations. Serving all Charles County residents.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited DC slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 45–55 min drive to downtown DC", fpc: "No — ship from La Plata" },
    { feature: "Military Travel Support", agency: "No flexibility for deployment needs", fpc: "Yes — we handle military cases" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
  ],
  mistakes: [
    {
      icon: "ri-time-line",
      title: "Waiting Until the Last Minute",
      description: "La Plata's busy county employees and military families often discover passport needs just weeks before departure. Routine processing takes 6–13 weeks. Start expedited service at least 6 weeks before your trip.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Local photo services in La Plata sometimes produce photos that meet general ID requirements but not passport standards. Passport photos must be exactly 2x2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 rejection reasons at La Plata and Charles County acceptance facilities.",
    },
    {
      icon: "ri-map-pin-2-line",
      title: "Not Booking the Acceptance Facility Early",
      description: "La Plata Post Office appointments fill up fast, especially during summer and holiday travel seasons. Book 2–3 weeks ahead.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Government fees change periodically. Always verify current fees before submitting — incorrect payment means starting over.",
    },
    {
      icon: "ri-time-line",
      title: "Underestimating Routine Processing Time",
      description: "La Plata residents often assume 4–6 weeks is enough. Routine processing takes 6–13 weeks. If your travel is within 10 weeks, use expedited service.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes — from your home in La Plata, Waldorf, or White Plains." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS or FedEx location near Charles St or in downtown Waldorf." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy — no rejections for small errors." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your La Plata address — trackable every step of the way." },
  ],
  faqs: [
    { q: "How fast can I get an emergency passport in La Plata, MD?", a: "Fast Passport Center offers emergency passport processing in La Plata, Maryland in as little as 24–48 hours. Our registered couriers hand-deliver your documents to the Washington DC Passport Agency (45–55 minutes from La Plata) — without you needing to make the long drive or fight Beltway traffic." },
    { q: "Does La Plata have its own passport agency?", a: "No — neither La Plata nor the state of Maryland has a regional passport agency. The closest facility is the Washington DC Passport Agency, approximately 45–55 minutes from La Plata via MD-210 and I-495. Fast Passport Center's courier service means La Plata residents never need to make that drive." },
    { q: "Can I get expedited passport services if I live in Waldorf or White Plains?", a: "Absolutely. Fast Passport Center serves all of the La Plata area including Waldorf, White Plains, Indian Head, Brandywine, and Bryans Road. We also serve Accokeek and Pomfret residents. Our service works the same way — complete your application online, ship your documents, and we handle the rest." },
    { q: "How much does an expedited passport cost in La Plata, MD?", a: "Government fees are fixed: $130 for a first-time passport book or $130 + $60 expedite for a renewed adult passport. Fast Passport Center's service fees vary by processing speed. Contact us for a quote based on your departure timeline." },
    { q: "What airports do La Plata travelers use for international flights?", a: "Most La Plata residents fly internationally through Reagan National (DCA), approximately 35–45 minutes north via MD-210 and I-495. Dulles International (IAD) is 45–55 minutes northwest. Baltimore-Washington International (BWI) is 45–55 minutes northeast. Always verify your passport is valid for at least 6 months past your return date." },
    { q: "Can I get a passport for my whole family in La Plata?", a: "Yes. Fast Passport Center handles multi-passport cases for families in La Plata, Waldorf, White Plains, and surrounding areas. Whether you need child passports, renewals, or first-time applications for the whole family, we coordinate the process to ensure everyone is ready to travel." },
    { q: "Where is the closest passport acceptance facility to La Plata?", a: "The La Plata Post Office at 40 St Mary's Ave is the primary downtown acceptance facility. The Waldorf Post Office at 3250 Leonardtown Rd serves northern residents. The Indian Head Post Office at 20 Strauss Ave serves western residents. All require appointments — book 1–2 weeks ahead." },
    { q: "How do I know if I need expedited or routine passport processing?", a: "If your travel is within 10 weeks, use expedited service. If your travel is within 2–3 weeks, use emergency expedited service. Routine processing takes 6–13 weeks and is only suitable for travel more than 3 months away. La Plata residents should plan ahead, especially for military deployments and family travel." },
  ],
  photoLocations: [
    { name: "Walgreens — La Plata", address: "40 Shoppers Way, La Plata, MD 20646", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Waldorf", address: "11770 Holly Ln, Waldorf, MD 20601", hours: "Mon–Sun: 8AM–10PM" },
    { name: "UPS Store — Waldorf", address: "3035 Waldorf Market Pl, Waldorf, MD 20601", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "FedEx Office — Waldorf", address: "3261 Leonardtown Rd, Waldorf, MD 20601", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
  ],
  dmvLocations: [
    { name: "MVA — Waldorf", address: "11 Industrial Park Dr, Waldorf, MD 20602", hours: "Mon–Fri: 8:30AM–4:30PM", note: "Serves Charles County and Southern Maryland. REAL ID processing available.", featured: true },
    { name: "MVA — Waldorf Express", address: "11 Industrial Park Dr, Waldorf, MD 20602", hours: "Mon–Fri: 8:30AM–4:30PM", note: "Serves Charles County and Southern Maryland. Full service including REAL ID.", featured: false },
  ],
  airports: [
    {
      code: "DCA",
      name: "Reagan National (Washington DC)",
      distance: "35–45 minutes from La Plata via MD-210 and I-495",
      address: "2401 Smith Blvd, Arlington, VA 22202",
      note: "Primary airport for La Plata travelers. Direct flights to major domestic hubs and some international destinations.",
      flightUrl: "https://www.flyreagan.com/",
      flightLabel: "DCA Flight Info",
      featured: true,
    },
    {
      code: "BWI",
      name: "Baltimore-Washington International Thurgood Marshall",
      distance: "45–55 minutes from La Plata via MD-210 and I-495 E",
      address: "7050 Friendship Rd, Baltimore, MD 21240",
      note: "Alternative for Southwest Airlines domestic connections and some Caribbean destinations.",
      flightUrl: "https://www.bwiairport.com/",
      flightLabel: "BWI Flight Info",
      featured: false,
    },
    {
      code: "IAD",
      name: "Dulles International",
      distance: "45–55 minutes from La Plata via MD-210 and I-495",
      address: "1 Saarinen Cir, Dulles, VA 20166",
      note: "Alternative for international flights. Direct flights to London, Frankfurt, and major European hubs.",
      flightUrl: "https://www.flydulles.com/",
      flightLabel: "IAD Flight Info",
      featured: false,
    },
  ],
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    { label: "Birth & Death Certificates (MD Vital Statistics)", url: "https://health.maryland.gov/vsa/", desc: "Maryland Department of Health — Vital Statistics Administration for birth and death certificates statewide" },
    { label: "Marriage Licenses (Charles County Clerk)", url: "https://www.charlescountymd.gov/", desc: "Charles County Clerk for La Plata and Charles County" },
    { label: "Divorce Decrees (Charles County Circuit Court)", url: "https://www.mdcourts.gov/circuit/charles", desc: "Charles County Circuit Court for divorce decree records" },
    { label: "VitalChek Online Ordering", url: "https://www.vitalchek.com/", desc: "Order Maryland vital records online via VitalChek" },
  ],
};